import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { leagueApi, LeaguePreview } from '../services/api-client';
import { useAuthContext } from '../contexts/AuthContext';
import { MobileLayout } from '../components/layout/MobileLayout';

export function JoinLeaguePage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuthContext();
  const allowRejoin = (location.state as { allowRejoin?: boolean })?.allowRejoin === true;

  const [league, setLeague] = useState<LeaguePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [membershipStatus, setMembershipStatus] = useState<
    'checking' | 'not-member' | 'already-member'
  >('checking');
  const [teamName, setTeamName] = useState('');
  const [step, setStep] = useState<'welcome' | 'team-setup'>('welcome');

  useEffect(() => {
    if (code) {
      loadLeaguePreview(code);
      // Store invite code in localStorage for post-auth redirect
      localStorage.setItem('pendingInviteCode', code);
    }
  }, [code]);

  useEffect(() => {
    // Pre-fill team name with user's first name
    if (profile?.displayName) {
      const firstName = profile.displayName.split(' ')[0];
      setTeamName(`${firstName}'s team`);
    }
  }, [profile]);

  // Re-check membership when user logs in (skip if joining via invite code)
  useEffect(() => {
    if (user && league?.id && !allowRejoin) {
      checkMembership(league.id);
    }
  }, [user, league?.id, allowRejoin]);

  const loadLeaguePreview = async (inviteCode: string) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch league preview (public endpoint, no auth required)
      const leagueData = await leagueApi.getByInviteCode(inviteCode);
      setLeague(leagueData);

      // If user is logged in, check if they're already a member
      // Skip membership check when joining via invite code (allows multiple teams)
      // Also skip if league allows multiple entries
      if (allowRejoin || leagueData.allowMultipleEntries) {
        setMembershipStatus('not-member');
      } else if (user) {
        checkMembership(leagueData.id);
      } else {
        setMembershipStatus('not-member');
      }
    } catch (err) {
      console.error('Failed to load league:', err);
      setError(err instanceof Error ? err.message : 'Failed to load league');
    } finally {
      setLoading(false);
    }
  };

  const checkMembership = async (leagueId: string) => {
    try {
      setMembershipStatus('checking');
      // Try to get the league - if user is a member, it will succeed
      await leagueApi.get(leagueId);
      setMembershipStatus('already-member');
    } catch (err: any) {
      // 403 means not a member (which is what we want)
      if (err.message?.includes('Forbidden') || err.message?.includes('access')) {
        setMembershipStatus('not-member');
      } else {
        // Some other error
        setMembershipStatus('not-member');
      }
    }
  };

  const handleJoinLeague = async () => {
    if (!code || !league) return;

    try {
      setJoining(true);
      const result = await leagueApi.join({
        inviteCode: code,
        teamName: teamName.trim() || undefined,
      });

      // Clear pending invite code from localStorage
      localStorage.removeItem('pendingInviteCode');

      // Navigate to the league page
      navigate(`/leagues/${league.id}`, {
        state: {
          joinStatus: result.status,
          showPendingMessage: result.status === 'pending',
        },
      });
    } catch (err) {
      console.error('Failed to join league:', err);
      alert(err instanceof Error ? err.message : 'Failed to join league');
      setJoining(false);
    }
  };

  // If not logged in, redirect to login with return path
  if (!user && !loading) {
    navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`);
    return null;
  }

  if (loading) {
    return (
      <MobileLayout showBottomNav={false}>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading league...</p>
          </div>
        </div>
      </MobileLayout>
    );
  }

  if (error) {
    return (
      <MobileLayout showBottomNav={false}>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/leagues')}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-shadow"
            >
              Back to Leagues
            </button>
          </div>
        </div>
      </MobileLayout>
    );
  }

  if (!league) {
    return null;
  }

  // Already a member
  if (membershipStatus === 'already-member') {
    return (
      <MobileLayout showBottomNav={false}>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">You're Already In!</h2>
            <p className="text-gray-600 mb-6">
              You're already a member of <span className="font-semibold">{league.name}</span>.
            </p>
            <button
              onClick={() => navigate(`/leagues/${league.id}`)}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-shadow"
            >
              View League
            </button>
          </div>
        </div>
      </MobileLayout>
    );
  }

  // Wizard
  return (
    <MobileLayout showBottomNav={false}>
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
          {step === 'welcome' ? (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                You've Been Invited!
              </h2>

              {/* League Preview Card */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 mb-6 border border-purple-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{league.name}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Budget per team:</span>
                    <span className="font-semibold text-gray-900">${league.budgetPerTeam}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Current members:</span>
                    <span className="font-semibold text-gray-900">{league.memberCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        league.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : league.status === 'draft'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {league.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* How It Works */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  How It Works
                </h4>
                <ol className="space-y-2 text-sm text-gray-600">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">
                      1
                    </span>
                    <span>
                      Draft wrestlers using your ${league.budgetPerTeam} budget (one per weight
                      class)
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">
                      2
                    </span>
                    <span>Earn points from real tournament results</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">
                      3
                    </span>
                    <span>Compete on the leaderboard</span>
                  </li>
                </ol>
              </div>

              {/* Continue Button */}
              <button
                onClick={() => setStep('team-setup')}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-shadow mb-3"
              >
                Continue
              </button>

              {/* Back to Leagues */}
              <button
                onClick={() => navigate('/leagues')}
                className="w-full text-gray-600 hover:text-gray-900 text-sm font-medium"
              >
                Back to Leagues
              </button>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Set Up Your Team
              </h2>

              {/* Team Name Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Team Name
                </label>
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  maxLength={200}
                  placeholder="Enter your team name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-purple-500 focus:border-purple-500"
                  autoFocus
                />
                <p className="mt-1 text-xs text-gray-500">You can change this later</p>
              </div>

              {/* Join Button */}
              <button
                onClick={handleJoinLeague}
                disabled={joining || membershipStatus === 'checking' || !teamName.trim()}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed mb-3"
              >
                {joining ? 'Joining...' : 'Join League'}
              </button>

              {/* Back Button */}
              <button
                onClick={() => setStep('welcome')}
                className="w-full text-gray-600 hover:text-gray-900 text-sm font-medium"
              >
                Back
              </button>
            </>
          )}
        </div>
      </div>
    </MobileLayout>
  );
}
